import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import {
  asId,
  type ProjectId,
  type StructuralElementId,
  type UnitPositionId,
  type WallId,
} from '@obraplan/core/domain/shared/ids';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import { createRoom, type Room } from '@obraplan/core/domain/takeoff/room';
import { sameItemFactors } from '@obraplan/core/domain/takeoff/own-factor';
import {
  createOpening,
  createWall,
  type Opening,
  type Wall,
  type WallOwner,
  type WallSide,
} from '@obraplan/core/domain/takeoff/wall';
import {
  createElementSegment,
  createStructuralElement,
  type ElementSegment,
  type StructuralElement,
} from '@obraplan/core/domain/takeoff/structural-element';
import { createShaft, type Shaft } from '@obraplan/core/domain/takeoff/shaft';
import type { PieceMark, PileType, StructuralPiece } from '@obraplan/core/domain/takeoff/pile';
import type { GroundBeam, PileCap } from '@obraplan/core/domain/takeoff/foundation';
import type { PileCapType } from '@obraplan/core/domain/takeoff/pile-cap-type';
import type { StructuralElementType } from '@obraplan/core/domain/takeoff/element-type';
import type { Slab } from '@obraplan/core/domain/takeoff/slab';
import type { RetainingColumn, RetainingWall } from '@obraplan/core/domain/takeoff/retaining';
import type { Stair } from '@obraplan/core/domain/takeoff/stair';
import { AppError, conflictError, validationError } from '../../errors/app-error';
import { saveProjectGeometrySchema } from '../../contracts/takeoff';
import { parseInput } from '../../contracts/parse';
import type { Clock } from '../../ports/clock';
import type {
  FloorPlanRepository,
  GeometryRevisionRepository,
  RoomRepository,
  ShaftRepository,
  StructuralElementRepository,
  UnitTypeRepository,
  WallRepository,
} from '../../ports/takeoff-repositories';
import type { TransactionRunner } from '../../ports/transaction';
import type { AuditService } from '../../services/audit-service';
import type { PermissionChecker } from '../../services/permission-checker';
import type { Session } from '../../session';
import { gravarPecas, lerPecas, planejarPecas, type GeometryPiecesDeps } from './geometry-pieces';
import {
  gravarTiposDeElemento,
  lerTiposDeElemento,
  planejarEstrutura,
  type GeometryElementTypesDeps,
} from './geometry-element-types';
import { conferirOndeMoram, type GeometryPlacementDeps } from './geometry-placement';

/**
 * O desenho da obra — leitura e gravação (Fase 14, fatia 3 · Fase 15).
 *
 * Ambiente, parede, vão, pilar e viga eram a última peça do quantitativo a viver no
 * `localStorage` do navegador. A espinha virou dado da obra na fatia 1, o catálogo e a
 * linha ganharam tabela na 2, e o desenho — o que mais demorou — entra aqui.
 *
 * ## Por que um envelope, e não vinte e cinco casos de uso
 *
 * A geometria é amarrada: a parede aponta dois ambientes, o trecho aponta parede e
 * elemento, e casar duas paredes **apaga uma e reescreve a outra**. Gravá-la ação por ação
 * deixaria o servidor com meia montagem entre uma requisição e a seguinte — parede
 * apontando ambiente que ainda não chegou, trecho apontando parede que acabou de sumir — e
 * cada uma dessas metades é um total errado na tela de alguém.
 *
 * Então é um envelope, numa transação, e a gravação **substitui**: o que não veio, saiu. É
 * a mesma semântica do rascunho que a tela sempre praticou, e é a que a montagem pede —
 * quem monta uma tipologia mexe em dez peças e espera que as dez valham juntas.
 *
 * ## O que isso custa, declarado
 *
 * **O último a gravar vence — só para quem não diz sobre qual revisão trabalhou.** Duas
 * pessoas montando a mesma tipologia ao mesmo tempo perdiam o trabalho de uma delas, sem
 * aviso (dois testadores, 07/10/2026: a E43 de uma aba sumia quando a outra gravava). Agora
 * a leitura devolve a revisão do desenho e a gravação a recebe em `baseRevision`: base que
 * não bate com a atual é recusada com 409 e nada se escreve. Continua não havendo mescla —
 * resolver conflito peça a peça exigiria versionar cada peça —, só a recusa de apagar o que
 * a outra aba gravou sem que a pessoa saiba.
 */

export interface GeometryDeps
  extends GeometryPiecesDeps, GeometryElementTypesDeps, GeometryPlacementDeps {
  readonly rooms: RoomRepository;
  readonly walls: WallRepository;
  readonly elements: StructuralElementRepository;
  /** O buraco na laje (Fase 24, D21) — do ambiente, e por ele da obra. */
  readonly shafts: ShaftRepository;
  /** A revisão do desenho da obra — a concorrência otimista da gravação (`0062`). */
  readonly revisions: GeometryRevisionRepository;
  /** Para conferir que o dono de cada peça é mesmo desta obra — ver `conferirEscopo`. */
  readonly unitTypes: UnitTypeRepository;
  readonly floorPlans: FloorPlanRepository;
  readonly permissions: PermissionChecker;
  readonly audit: AuditService;
  readonly tx: TransactionRunner;
  readonly clock: Clock;
}

/** O código do 409 de `SaveProjectGeometry` — a bancada reconhece a recusa por ele. */
export const GEOMETRY_CONFLICT = 'TAKEOFF.GEOMETRY_CONFLICT';

const MENSAGEM_DO_CONFLITO =
  'O desenho desta obra mudou em outra aba ou por outra pessoa desde que você abriu. ' +
  'Recarregue para ver a versão atual — sua última alteração não foi gravada.';

/**
 * O desenho como a leitura o devolve: com a revisão dele (`0062`). Quem grava devolve essa
 * revisão em `baseRevision`, e é ela que impede a aba desatualizada de apagar o que outra
 * gravou.
 */
export interface ProjectGeometryRead extends ProjectGeometry {
  readonly revision: number;
}

/** O desenho de uma obra, como a bancada o lê. */
export interface ProjectGeometry {
  readonly rooms: readonly Room[];
  readonly walls: readonly Wall[];
  readonly openings: readonly Opening[];
  readonly elements: readonly StructuralElement[];
  readonly segments: readonly ElementSegment[];
  /**
   * Os tipos de pilar e de viga da obra (Fase 42, RN-561). A leitura sempre os traz; ausente é a
   * gravação que não disse nada sobre eles — fica o gravado.
   */
  readonly elementTypes?: readonly StructuralElementType[];
  /**
   * Os shafts (Fase 24, D21). A leitura sempre os traz; ausente é a gravação que não disse
   * nada sobre eles — ver `saveProjectGeometrySchema` (RN-272).
   */
  readonly shafts?: readonly Shaft[];
  /**
   * Os tipos de estaca da obra, as estacas e as marcas por serviço (Fase 27, etapa 1b). A
   * leitura sempre os traz; ausente é a gravação que não disse nada — ver `geometry-pieces`.
   * A marca vai pela chave do serviço no catálogo, como o rascunho a guarda.
   */
  readonly pileTypes?: readonly PileType[];
  readonly pieces?: readonly StructuralPiece[];
  readonly pieceMarks?: readonly PieceMark[];
  /** Os tipos de bloco da obra (Fase 41, RN-536) — a mesma regra dos tipos de estaca. */
  readonly pileCapTypes?: readonly PileCapType[];
  /** Os blocos e as vigas baldrame (Fase 27, etapa 2) — a mesma regra da estaca. */
  readonly pileCaps?: readonly PileCap[];
  readonly groundBeams?: readonly GroundBeam[];
  /** O radier e a laje (Fase 27, etapa 3) — a mesma regra do bloco. */
  readonly slabs?: readonly Slab[];
  /** O muro e o pilar de contenção (Fase 27, etapa 4) — a mesma regra do bloco. */
  readonly retainingWalls?: readonly RetainingWall[];
  readonly retainingColumns?: readonly RetainingColumn[];
  /** A escada (Fase 27, etapa 5) — a mesma regra do bloco. */
  readonly stairs?: readonly Stair[];
}

// ── Leitura ─────────────────────────────────────────────────────────────────────

export class GetProjectGeometry {
  constructor(private readonly deps: GeometryDeps) {}

  async execute(
    projectId: ProjectId,
    session: Session,
  ): Promise<Result<ProjectGeometryRead, AppError>> {
    const allowed = this.deps.permissions.ensureForProject(
      session,
      PERMISSION.TAKEOFF_VIEW,
      projectId,
    );
    if (!allowed.ok) return allowed;

    // A revisão vem **antes** do desenho, e não junto: uma gravação que confirme no meio da
    // leitura deixaria revisão velha com desenho novo — o que só gera um 409 a mais. A ordem
    // contrária daria revisão nova com desenho velho, e a aba gravaria por cima sem saber.
    const revision = await this.deps.revisions.read(projectId);

    // Consultas independentes, e fora de transação o pool as atende de verdade ao mesmo
    // tempo — como `GetProjectTakeoff` já faz com as três dela. A fundação é mais uma.
    const [rooms, walls, openings, elements, segments, shafts, pecas, elementTypes] =
      await Promise.all([
        this.deps.rooms.listByProject(projectId),
        this.deps.walls.listByProject(projectId),
        this.deps.walls.listOpeningsByProject(projectId),
        this.deps.elements.listByProject(projectId),
        this.deps.elements.listSegmentsByProject(projectId),
        this.deps.shafts.listByProject(projectId),
        lerPecas(this.deps, projectId),
        // O tipo de pilar e de viga (Fase 42, RN-561): o elemento já volta com o dele e o fator.
        lerTiposDeElemento(this.deps, projectId),
      ]);

    return ok({
      rooms,
      walls,
      openings,
      elements,
      elementTypes,
      segments,
      shafts,
      ...pecas,
      revision,
    });
  }
}

// ── Gravação ────────────────────────────────────────────────────────────────────

type Entrada = ReturnType<typeof saveProjectGeometrySchema.parse>;

/**
 * O que foi gravado, para a tela saber que chegou e a auditoria dizer o tamanho.
 *
 * Contagens, e não a geometria de volta: quem gravou já tem o desenho na mão, e devolvê-lo
 * dobraria o tráfego da montagem inteira para repetir o que o cliente acabou de mandar.
 */
export interface GeometrySaved {
  readonly rooms: number;
  readonly walls: number;
  readonly openings: number;
  readonly elements: number;
  readonly segments: number;
  /** Os tipos de pilar e de viga que o envelope trouxe — zero quando não disse (Fase 42). */
  readonly elementTypes: number;
  /** Os shafts que o envelope trouxe — zero quando ele não disse nada sobre eles. */
  readonly shafts: number;
  /** Os tipos de estaca, as estacas e as marcas que o envelope trouxe (Fase 27). */
  readonly pileTypes: number;
  readonly pieces: number;
  readonly pieceMarks: number;
  /** As marcas de serviço que a obra não alcança, deixadas de fora — ver `geometry-pieces`. */
  readonly skippedMarks: number;
  /** Os blocos e os baldrames que o envelope trouxe (Fase 27, etapa 2). */
  readonly pileCaps: number;
  readonly groundBeams: number;
  /** Os radiers e as lajes que o envelope trouxe (Fase 27, etapa 3). */
  readonly slabs: number;
  /** Os muros e os pilares de contenção que o envelope trouxe (Fase 27, etapa 4). */
  readonly retainingWalls: number;
  readonly retainingColumns: number;
  /** As escadas que o envelope trouxe (Fase 27, etapa 5). */
  readonly stairs: number;
  /** Peças que sumiram do envelope e foram marcadas como excluídas. */
  readonly removed: number;
  /** A revisão do desenho depois desta gravação — a base da próxima. */
  readonly revision: number;
}

export class SaveProjectGeometry {
  constructor(private readonly deps: GeometryDeps) {}

  async execute(input: unknown, session: Session): Promise<Result<GeometrySaved, AppError>> {
    const parsed = parseInput(saveProjectGeometrySchema, input);
    if (!parsed.ok) return parsed;

    const projectId = asId<'Project'>(parsed.value.projectId) as ProjectId;
    const allowed = this.deps.permissions.ensureForProject(
      session,
      PERMISSION.TAKEOFF_MANAGE,
      projectId,
    );
    if (!allowed.ok) return allowed;

    const coerente = conferirAmarracao(parsed.value);
    if (!coerente.ok) return coerente;

    return this.deps.tx.run(async () => {
      // A primeira coisa da transação, antes de ler qualquer linha: trava a obra e confere que
      // quem grava trabalhou sobre o desenho que está lá. Sem a trava, duas gravações
      // simultâneas leriam a mesma revisão e a última venceria; sem a conferência, a aba que
      // não recarregou apagaria o que a outra gravou (07/10/2026).
      const atual = await this.deps.revisions.lock(projectId);
      const base = parsed.value.baseRevision;
      if (base !== undefined && base !== atual) {
        return err(
          conflictError(GEOMETRY_CONFLICT, MENSAGEM_DO_CONFLITO, {
            baseRevision: base,
            currentRevision: atual,
          }),
        );
      }

      const escopo = await conferirEscopo(this.deps, projectId, parsed.value);
      if (!escopo.ok) return escopo;
      // Onde cada peça mora, antes da primeira escrita (RN-361) — a tela só esconde o botão.
      const lugares = await conferirOndeMoram(this.deps, projectId, parsed.value);
      if (!lugares.ok) return lugares;

      const agora = this.deps.clock.nowIso();
      const [existentes, paredes, elementos, shafts] = await Promise.all([
        this.deps.rooms.listByProject(projectId),
        this.deps.walls.listByProject(projectId),
        this.deps.elements.listByProject(projectId),
        this.deps.shafts.listByProject(projectId),
      ]);

      const nascidoEm = new Map<string, string>([
        ...existentes.map((room): [string, string] => [room.id, room.createdAt]),
        ...paredes.map((wall): [string, string] => [wall.id, wall.createdAt]),
        ...elementos.map((el): [string, string] => [el.id, el.createdAt]),
        ...shafts.map((shaft): [string, string] => [shaft.id, shaft.createdAt]),
      ]);

      // O fator próprio do cômodo que o envelope não disse fica o gravado (Fase 39, RN-512).
      const fatoresGravados = new Map(
        existentes.map((room): [string, Room['factors']] => [room.id, room.factors]),
      );
      const montados = montar(parsed.value, nascidoEm, agora, fatoresGravados);
      if (!montados.ok) return montados;
      // A fundação conferida antes da primeira escrita: recusa no meio deixaria meia gravação
      // para a transação desfazer (Fase 27, etapa 1b).
      const pecas = await planejarPecas(this.deps, projectId, parsed.value, agora);
      if (!pecas.ok) return pecas;
      // O tipo e o fator do pilar e da viga (Fase 42, RN-561 a RN-566), também antes de escrever.
      const estrutura = await planejarEstrutura(
        this.deps,
        projectId,
        parsed.value,
        elementos,
        agora,
      );
      if (!estrutura.ok) return estrutura;
      const doElemento = estrutura.value.doElemento;

      await this.deps.rooms.saveMany(montados.value.rooms);
      await this.deps.walls.saveMany(montados.value.walls);
      // O tipo antes do elemento que o aponta.
      await gravarTiposDeElemento(this.deps, estrutura.value);
      await this.deps.elements.saveMany(
        montados.value.elements.map((element) => ({ ...element, ...doElemento.get(element.id) })),
      );
      // Depois do ambiente, que o shaft referencia (RN-272).
      await this.deps.shafts.saveMany(montados.value.shafts ?? []);
      // A estaca não aponta ambiente nem parede: vai sozinha, com o tipo e as marcas.
      await gravarPecas(this.deps, projectId, pecas.value);

      // Os filhos vão depois dos pais: o vão referencia a parede, o trecho referencia o
      // elemento **e** a parede, e o banco confere a chave estrangeira na hora do INSERT.
      for (const [wallId, vaos] of porPai(montados.value.openings, (o) => o.wallId)) {
        await this.deps.walls.replaceOpenings(asId<'Wall'>(wallId), vaos);
      }
      for (const [wallId] of paredesSemVao(montados.value)) {
        await this.deps.walls.replaceOpenings(wallId, []);
      }
      for (const [elementId, trechos] of porPai(montados.value.segments, (s) => s.elementId)) {
        await this.deps.elements.replaceSegments(asId<'StructuralElement'>(elementId), trechos);
      }
      for (const elementId of elementosSemTrecho(montados.value)) {
        await this.deps.elements.replaceSegments(elementId, []);
      }

      const removed = await apagarOsQueSumiram(
        this.deps,
        { existentes, paredes, elementos, shafts },
        parsed.value,
        agora,
      );

      const resumo: GeometrySaved = {
        rooms: montados.value.rooms.length,
        walls: montados.value.walls.length,
        openings: montados.value.openings.length,
        elements: montados.value.elements.length,
        segments: montados.value.segments.length,
        elementTypes: estrutura.value.tiposGravar.length,
        shafts: montados.value.shafts?.length ?? 0,
        pileTypes: pecas.value.tiposGravar.length,
        pieces: pecas.value.pecasGravar.length,
        pieceMarks: pecas.value.marcas?.marks.length ?? 0,
        skippedMarks: pecas.value.marcasIgnoradas,
        pileCaps: pecas.value.blocosGravar.length,
        groundBeams: pecas.value.baldramesGravar.length,
        slabs: pecas.value.lajesGravar.length,
        retainingWalls: pecas.value.murosGravar.length,
        retainingColumns: pecas.value.pilaresGravar.length,
        stairs: pecas.value.escadasGravar.length,
        removed:
          removed +
          estrutura.value.tiposApagar.length +
          pecas.value.pecasApagar.length +
          pecas.value.tiposApagar.length +
          pecas.value.blocosApagar.length +
          pecas.value.baldramesApagar.length +
          pecas.value.lajesApagar.length +
          pecas.value.murosApagar.length +
          pecas.value.pilaresApagar.length +
          pecas.value.escadasApagar.length,
        // Soma um depois de tudo escrito: a transação confirma as linhas e a revisão juntas.
        revision: await this.deps.revisions.bump(projectId),
      };

      // Um registro por gravação, e não um por peça: o que aconteceu foi "alguém salvou a
      // montagem", e trezentas linhas de auditoria diriam isso trezentas vezes.
      await this.deps.audit.record({
        action: 'UPDATE',
        entityType: 'ProjectGeometry',
        entityId: projectId,
        actorUserId: session.userId,
        projectId,
        // As peças de antes da regra que continuam fora do lugar: passam, mas ficam no registro.
        metadata: {
          ...resumo,
          ...(lugares.value.length === 0 ? {} : { pecasForaDoLugar: lugares.value }),
        },
      });

      return ok(resumo);
    });
  }
}

// ── As conferências ─────────────────────────────────────────────────────────────

/**
 * O envelope aponta só para dentro de si mesmo?
 *
 * A gravação é substituição, então tudo o que a geometria referencia tem de vir junto. Uma
 * parede apontando ambiente que ficou de fora atravessaria a validação de forma, estouraria
 * na chave estrangeira e voltaria como erro cru de banco — em inglês, no meio de uma
 * montagem de tarde inteira.
 *
 * Roda **fora** da transação de propósito: não depende do banco, e recusar antes de abrir
 * transação é recusar mais barato.
 */
function conferirAmarracao(entrada: Entrada): Result<true, AppError> {
  const ambientes = new Set(entrada.rooms.map((room) => room.id));
  const paredes = new Set(entrada.walls.map((wall) => wall.id));
  const elementos = new Set(entrada.elements.map((element) => element.id));

  for (const wall of entrada.walls) {
    for (const lado of [wall.sideA, wall.sideB]) {
      if (lado !== undefined && !ambientes.has(lado.roomId)) {
        return err(
          validationError(
            'TAKEOFF.GEOMETRY_INCOMPLETE',
            'Uma parede aponta um ambiente que não veio no desenho.',
            [{ field: 'walls', message: `Parede ${wall.id} aponta o ambiente ${lado.roomId}.` }],
          ),
        );
      }
    }
  }

  for (const opening of entrada.openings) {
    if (!paredes.has(opening.wallId)) {
      return err(
        validationError(
          'TAKEOFF.GEOMETRY_INCOMPLETE',
          'Um vão aponta uma parede que não veio no desenho.',
          [{ field: 'openings', message: `Vão ${opening.id} aponta a parede ${opening.wallId}.` }],
        ),
      );
    }
  }

  for (const segment of entrada.segments) {
    if (!elementos.has(segment.elementId) || !paredes.has(segment.wallId)) {
      return err(
        validationError(
          'TAKEOFF.GEOMETRY_INCOMPLETE',
          'Um trecho de pilar ou viga aponta peça que não veio no desenho.',
          [
            {
              field: 'segments',
              message: `Trecho ${segment.id} aponta o elemento ${segment.elementId} na parede ${segment.wallId}.`,
            },
          ],
        ),
      );
    }
  }

  // O shaft aponta o ambiente de que desconta, e é por ele que chega à obra: um ambiente de
  // fora do envelope seria, além de chave estrangeira quebrada, a porta dos fundos da
  // conferência de escopo — que confere o ambiente, e não o shaft (RN-272).
  for (const shaft of entrada.shafts ?? []) {
    if (!ambientes.has(shaft.roomId)) {
      return err(
        validationError(
          'TAKEOFF.GEOMETRY_INCOMPLETE',
          'Um shaft aponta um ambiente que não veio no desenho.',
          [{ field: 'shafts', message: `Shaft ${shaft.id} aponta o ambiente ${shaft.roomId}.` }],
        ),
      );
    }
  }

  return ok(true);
}

/**
 * Tudo o que chegou é mesmo desta obra?
 *
 * **É autorização, e não integridade.** A permissão foi conferida contra o `projectId` do
 * envelope, e o ambiente traz o `unitTypeId` dele dentro: sem esta conferência, quem pode
 * montar a obra A mandaria um ambiente apontando a tipologia da obra B e o gravaria lá —
 * com permissão legítima, numa obra que ele não alcança.
 *
 * O mesmo vale para o dono da parede e do elemento, que é tipologia ou pavimento tipo.
 */
async function conferirEscopo(
  deps: GeometryDeps,
  projectId: ProjectId,
  entrada: Entrada,
): Promise<Result<true, AppError>> {
  const [tipologias, desenhos] = await Promise.all([
    deps.unitTypes.listByProject(projectId),
    deps.floorPlans.listByProject(projectId),
  ]);
  const daObra = {
    tipologias: new Set<string>(tipologias.map((tipologia) => tipologia.id)),
    desenhos: new Set<string>(desenhos.map((desenho) => desenho.id)),
  };

  const foraDaObra = (owner: {
    kind: string;
    unitTypeId?: string;
    floorPlanId?: string;
  }): boolean =>
    owner.kind === 'UNIT_TYPE'
      ? !daObra.tipologias.has(owner.unitTypeId!)
      : !daObra.desenhos.has(owner.floorPlanId!);

  const recusa = (o_que: string, id: string): Result<true, AppError> =>
    err(
      validationError(
        'TAKEOFF.OUT_OF_PROJECT',
        'O desenho aponta uma tipologia ou um pavimento tipo de outra obra.',
        [{ field: o_que, message: `${o_que} ${id} não pertence a esta obra.` }],
      ),
    );

  for (const room of entrada.rooms) {
    if (!daObra.tipologias.has(room.unitTypeId)) return recusa('rooms', room.id);
  }
  for (const wall of entrada.walls) {
    if (foraDaObra(wall.owner)) return recusa('walls', wall.id);
  }
  for (const element of entrada.elements) {
    if (foraDaObra(element.owner)) return recusa('elements', element.id);
  }
  // A estaca é da área comum de uma tipologia (RN-361), e chega à obra por ela.
  for (const piece of entrada.pieces ?? []) {
    if (!daObra.tipologias.has(piece.unitTypeId)) return recusa('pieces', piece.id);
  }
  // O bloco e o baldrame também (etapa 2).
  for (const cap of entrada.pileCaps ?? []) {
    if (!daObra.tipologias.has(cap.unitTypeId)) return recusa('pileCaps', cap.id);
  }
  for (const beam of entrada.groundBeams ?? []) {
    if (!daObra.tipologias.has(beam.unitTypeId)) return recusa('groundBeams', beam.id);
  }
  // O radier e a laje (etapa 3): a área comum de uma tipologia desta obra (RN-361).
  for (const slab of entrada.slabs ?? []) {
    if (!daObra.tipologias.has(slab.unitTypeId)) return recusa('slabs', slab.id);
  }
  // O muro e o pilar de contenção (etapa 4): a área comum de uma tipologia desta obra.
  for (const wall of entrada.retainingWalls ?? []) {
    if (!daObra.tipologias.has(wall.unitTypeId)) return recusa('retainingWalls', wall.id);
  }
  for (const column of entrada.retainingColumns ?? []) {
    if (!daObra.tipologias.has(column.unitTypeId)) return recusa('retainingColumns', column.id);
  }
  // A escada (etapa 5): a área comum de uma tipologia desta obra, como a laje.
  for (const stair of entrada.stairs ?? []) {
    if (!daObra.tipologias.has(stair.unitTypeId)) return recusa('stairs', stair.id);
  }

  return ok(true);
}

// ── A montagem ──────────────────────────────────────────────────────────────────

/**
 * O envelope virando entidades de domínio.
 *
 * Passa por `createRoom`, `createWall` e companhia em vez de montar o objeto na mão: são
 * elas que carregam as regras — nome vazio, medida não positiva, vão com contagem
 * fracionária. Repeti-las aqui criaria uma segunda validação para divergir da primeira.
 *
 * `createdAt` é preservado do que já estava gravado; só o `updatedAt` é novo. A peça que
 * atravessa uma montagem inteira sem mudar não pode aparecer na auditoria como recém-criada.
 */
function montar(
  entrada: Entrada,
  nascidoEm: ReadonlyMap<string, string>,
  agora: string,
  fatoresGravados: ReadonlyMap<string, Room['factors']> = new Map(),
): Result<ProjectGeometry, AppError> {
  const rooms: Room[] = [];
  for (const bruto of entrada.rooms) {
    // O fator próprio do cômodo (Fase 39, RN-512): ausente, fica o gravado — a tela de antes da
    // etapa 3 não o conhece. Conferido sempre, mas só vai ao repositório o que mudou: presente é
    // "a lista do cômodo é esta" (a vazia apaga), e o cômodo igual ao gravado não paga a troca.
    const gravados = fatoresGravados.get(bruto.id);
    // A origem e a foto andam com o fator (Fase 40, RN-527), sem os opcionais vazios do contrato.
    const fatores =
      bruto.factors?.map((linha) => ({
        serviceKey: linha.serviceKey,
        itemKey: linha.itemKey,
        factor: linha.factor,
        ...(linha.origin === undefined ? {} : { origin: linha.origin }),
        ...(linha.average === undefined ? {} : { average: linha.average }),
      })) ??
      gravados ??
      [];
    const criado = createRoom({
      id: asId<'Room'>(bruto.id),
      unitTypeId: asId<'UnitType'>(bruto.unitTypeId),
      name: bruto.name,
      kind: bruto.kind,
      width: bruto.width,
      length: bruto.length,
      ceilingHeight: bruto.ceilingHeight,
      now: agora,
      ...(bruto.code === undefined ? {} : { code: bruto.code }),
      ...(bruto.outline === undefined ? {} : { outline: bruto.outline }),
      ...(bruto.edgesWithoutWall === undefined ? {} : { edgesWithoutWall: bruto.edgesWithoutWall }),
      ...(bruto.areaOverride === undefined ? {} : { areaOverride: bruto.areaOverride }),
      ...(bruto.perimeterOverride === undefined
        ? {}
        : { perimeterOverride: bruto.perimeterOverride }),
      ...(bruto.notes === undefined ? {} : { notes: bruto.notes }),
      factors: fatores,
    });
    if (!criado.ok) return err(AppError.fromDomain(criado.error));
    const { factors: conferidos, ...semFatores } = criado.value;
    rooms.push({
      ...semFatores,
      ...(conferidos === undefined || sameItemFactors(conferidos, gravados)
        ? {}
        : { factors: conferidos }),
      createdAt: nascidoEm.get(bruto.id) ?? agora,
    });
  }

  const walls: Wall[] = [];
  for (const bruto of entrada.walls) {
    const criada = createWall({
      id: asId<'Wall'>(bruto.id),
      owner: paraDono(bruto.owner),
      sideA: paraLado(bruto.sideA),
      length: bruto.length,
      height: bruto.height,
      now: agora,
      ...(bruto.sideB === undefined ? {} : { sideB: paraLado(bruto.sideB) }),
      ...(bruto.thickness === undefined ? {} : { thickness: bruto.thickness }),
      ...(bruto.railingHeight === undefined ? {} : { railingHeight: bruto.railingHeight }),
      ...(bruto.label === undefined ? {} : { label: bruto.label }),
      ...(bruto.notes === undefined ? {} : { notes: bruto.notes }),
    });
    if (!criada.ok) return err(AppError.fromDomain(criada.error));
    // `joined` não entra em `createWall`: conjugar é uma decisão posterior ao cadastro, e
    // o domínio a trata como transição (`joinWall`). Aqui ela chega junto com o resto.
    walls.push({
      ...criada.value,
      joined: bruto.joined,
      createdAt: nascidoEm.get(bruto.id) ?? agora,
    });
  }

  const openings: Opening[] = [];
  for (const bruto of entrada.openings) {
    const criado = createOpening({
      id: asId<'Opening'>(bruto.id),
      wallId: asId<'Wall'>(bruto.wallId),
      kind: bruto.kind,
      width: bruto.width,
      height: bruto.height,
      count: bruto.count,
      ...(bruto.offset === undefined ? {} : { offset: bruto.offset }),
    });
    if (!criado.ok) return err(AppError.fromDomain(criado.error));
    openings.push(criado.value);
  }

  const elements: StructuralElement[] = [];
  for (const bruto of entrada.elements) {
    const criado = createStructuralElement({
      id: asId<'StructuralElement'>(bruto.id),
      owner: paraDono(bruto.owner),
      kind: bruto.kind,
      sectionWidth: bruto.sectionWidth,
      sectionDepth: bruto.sectionDepth,
      length: bruto.length,
      now: agora,
      ...(bruto.name === undefined ? {} : { name: bruto.name }),
      ...(bruto.freeSpan === undefined ? {} : { freeSpan: bruto.freeSpan }),
      // O contorno passa por `createStructuralElement`, que o confere (`checkElementOutline`).
      // Pilar sem trecho nenhum é válido quando há contorno: ele sabe onde está sozinho, e
      // esta montagem nunca exigiu trecho (RN-190: é o trecho que desconta, não o elemento).
      ...(bruto.outline === undefined ? {} : { outline: bruto.outline }),
      ...(bruto.notes === undefined ? {} : { notes: bruto.notes }),
    });
    if (!criado.ok) return err(AppError.fromDomain(criado.error));
    elements.push({ ...criado.value, createdAt: nascidoEm.get(bruto.id) ?? agora });
  }

  const segments: ElementSegment[] = [];
  for (const bruto of entrada.segments) {
    const criado = createElementSegment({
      id: asId<'ElementSegment'>(bruto.id),
      elementId: asId<'StructuralElement'>(bruto.elementId),
      wallId: asId<'Wall'>(bruto.wallId),
      orientation: bruto.orientation,
      ...(bruto.length === undefined ? {} : { length: bruto.length }),
      // Ausente continua ausente: é o avanço derivado, e mandá-lo como zero aqui diria
      // "rente" onde o cadastro diz "20 numa parede de 15" (RN-187).
      ...(bruto.advance === undefined ? {} : { advance: bruto.advance }),
      ...(bruto.advanceSide === undefined ? {} : { advanceSide: bruto.advanceSide }),
      ...(bruto.offset === undefined ? {} : { offset: bruto.offset }),
      ...(bruto.lateral === undefined ? {} : { lateral: bruto.lateral }),
    });
    if (!criado.ok) return err(AppError.fromDomain(criado.error));
    segments.push(criado.value);
  }

  // Envelope que não disse nada sobre o shaft volta sem eles — e quem os mantém é
  // `apagarOsQueSumiram`, que só tira o do ambiente que saiu (RN-272).
  if (entrada.shafts === undefined) return ok({ rooms, walls, openings, elements, segments });

  const shafts: Shaft[] = [];
  for (const bruto of entrada.shafts) {
    const criado = createShaft({
      id: asId<'Shaft'>(bruto.id),
      roomId: asId<'Room'>(bruto.roomId),
      from: bruto.from,
      to: bruto.to,
      count: bruto.count,
      now: agora,
      ...(bruto.name === undefined ? {} : { name: bruto.name }),
      ...(bruto.notes === undefined ? {} : { notes: bruto.notes }),
    });
    if (!criado.ok) return err(AppError.fromDomain(criado.error));
    shafts.push({ ...criado.value, createdAt: nascidoEm.get(bruto.id) ?? agora });
  }

  return ok({ rooms, walls, openings, elements, segments, shafts });
}

function paraDono(owner: Entrada['walls'][number]['owner']): WallOwner {
  return owner.kind === 'UNIT_TYPE'
    ? { kind: 'UNIT_TYPE', unitTypeId: asId<'UnitType'>(owner.unitTypeId) }
    : { kind: 'FLOOR_PLAN', floorPlanId: asId<'FloorPlan'>(owner.floorPlanId) };
}

function posicao(positionId: string | undefined): { positionId?: UnitPositionId } {
  return positionId === undefined ? {} : { positionId: asId<'UnitPosition'>(positionId) };
}

/**
 * Um lado da parede, do envelope para o domínio — com o lado do contorno, quando veio.
 *
 * Ausente continua ausente: é a parede de antes do índice (RN-244), e mandá-la como zero
 * aqui a poria no primeiro lado do ambiente, por cima de outra.
 */
function paraLado(bruto: Entrada['walls'][number]['sideA']): WallSide {
  return {
    roomId: asId<'Room'>(bruto.roomId),
    ...posicao(bruto.positionId),
    ...(bruto.edgeIndex === undefined ? {} : { edgeIndex: bruto.edgeIndex }),
    // O trecho do lado (RN-304). Ausente é o lado inteiro, que é o que toda parede foi antes
    // do corte da sobreposição parcial.
    ...(bruto.span === undefined ? {} : { span: { start: bruto.span.start, end: bruto.span.end } }),
  };
}

// ── O que sumiu ─────────────────────────────────────────────────────────────────

function porPai<T>(
  filhos: readonly T[],
  paiDe: (filho: T) => string,
): ReadonlyMap<string, readonly T[]> {
  const agrupados = new Map<string, T[]>();
  for (const filho of filhos) {
    const lista = agrupados.get(paiDe(filho));
    if (lista === undefined) agrupados.set(paiDe(filho), [filho]);
    else lista.push(filho);
  }
  return agrupados;
}

/**
 * As paredes que ficaram sem vão nenhum.
 *
 * Precisam de uma passada explícita: `porPai` só visita quem tem filho, e a parede cujo
 * último vão foi removido não aparece em lista nenhuma. Sem isto, apagar a porta na tela
 * não a apagaria no banco — e ela continuaria descontando alvenaria.
 */
function paredesSemVao(geometria: ProjectGeometry): readonly (readonly [WallId])[] {
  const comVao = new Set(geometria.openings.map((opening) => opening.wallId));
  return geometria.walls.filter((wall) => !comVao.has(wall.id)).map((wall) => [wall.id] as const);
}

/** O par de `paredesSemVao`, pelo mesmo motivo: o elemento que saiu da última parede. */
function elementosSemTrecho(geometria: ProjectGeometry): readonly StructuralElementId[] {
  const comTrecho = new Set(geometria.segments.map((segment) => segment.elementId));
  return geometria.elements
    .filter((element) => !comTrecho.has(element.id))
    .map((element) => element.id);
}

/**
 * O que estava gravado e não veio no envelope sai de cena.
 *
 * **Apagamento suave**, como em todo o sistema: a peça fica com `deletedAt` e some da
 * leitura. É o que permite descobrir, semanas depois, que a sala foi removida — e por quem.
 *
 * O vão e o trecho não aparecem aqui porque já saíram: `replaceOpenings` e
 * `replaceSegments` trocaram a lista inteira, e filho sem pai no envelope morre com o pai.
 */
async function apagarOsQueSumiram(
  deps: GeometryDeps,
  gravado: {
    readonly existentes: readonly Room[];
    readonly paredes: readonly Wall[];
    readonly elementos: readonly StructuralElement[];
    readonly shafts: readonly Shaft[];
  },
  entrada: Entrada,
  agora: string,
): Promise<number> {
  const chegaram = {
    rooms: new Set(entrada.rooms.map((room) => room.id)),
    walls: new Set(entrada.walls.map((wall) => wall.id)),
    elements: new Set(entrada.elements.map((element) => element.id)),
    shafts:
      entrada.shafts === undefined
        ? undefined
        : new Set<string>(entrada.shafts.map((shaft) => shaft.id)),
  };

  const ambientes = gravado.existentes.filter((room) => !chegaram.rooms.has(room.id));
  const paredes = gravado.paredes.filter((wall) => !chegaram.walls.has(wall.id));
  const elementos = gravado.elementos.filter((element) => !chegaram.elements.has(element.id));
  // O envelope que não falou de shaft não tira nenhum — só o do ambiente que saiu, porque
  // buraco sem chão não desconta de ninguém (RN-272).
  const shafts = gravado.shafts.filter((shaft) =>
    chegaram.shafts === undefined
      ? !chegaram.rooms.has(shaft.roomId)
      : !chegaram.shafts.has(shaft.id),
  );

  // A ordem é a inversa da gravação: o filho sai antes do pai, e o pai antes de quem ele
  // aponta. Parede removida leva os vãos dela; elemento removido leva os trechos.
  for (const element of elementos) await deps.elements.replaceSegments(element.id, []);
  for (const wall of paredes) await deps.walls.replaceOpenings(wall.id, []);

  await deps.shafts.saveMany(
    shafts.map((shaft) => ({ ...shaft, deletedAt: agora, updatedAt: agora })),
  );
  await deps.elements.saveMany(
    elementos.map((el) => ({ ...el, deletedAt: agora, updatedAt: agora })),
  );
  await deps.walls.saveMany(
    paredes.map((wall) => ({ ...wall, deletedAt: agora, updatedAt: agora })),
  );
  await deps.rooms.saveMany(
    ambientes.map((room) => ({ ...room, deletedAt: agora, updatedAt: agora })),
  );

  return ambientes.length + paredes.length + elementos.length + shafts.length;
}
